import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PackageSearch, ChevronDown, ShieldCheck, MapPin, ShoppingCart, Banknote } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError, SAR } from "@/lib/api";
import { Loader, Empty, Stars } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const REQ_STATUS = {
  open: ["مفتوح — بانتظار العروض", "#F59E0B"],
  accepted: ["تم قبول عرض", "#10B981"],
  closed: ["مغلق", "#6B7280"],
};

export default function MyRequests() {
  const [reqs, setReqs] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const load = () => api.get("/part-requests/mine").then(({ data }) => setReqs(data));
  useEffect(() => { load(); }, []);

  if (reqs === null) return <Loader />;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-head text-2xl font-black text-white flex items-center gap-2"><PackageSearch size={24} className="text-[var(--amber)]" /> طلبات القطع والعروض</h1>
        <Link to="/request-part" data-testid="new-request-link" className="btn-amber !py-2 !px-4 text-sm">+ طلب جديد</Link>
      </div>

      {reqs.length === 0 ? (
        <Empty text="لا توجد طلبات قطع بعد. أرسل طلبًا وستصلك عروض من التشاليح." />
      ) : (
        <div className="space-y-4">
          {reqs.map((r) => (
            <RequestCard key={r.id} r={r} expanded={expanded === r.id}
              onToggle={() => setExpanded(expanded === r.id ? null : r.id)} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function RequestCard({ r, expanded, onToggle, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [lbl, color] = REQ_STATUS[r.status] || REQ_STATUS.open;

  useEffect(() => {
    if (expanded) api.get(`/part-requests/${r.id}`).then(({ data }) => setDetail(data));
  }, [expanded, r.id]);

  return (
    <div className="ts-card p-4" data-testid={`request-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-white">{r.part_name}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">{r.make} {r.model} {r.year ? `· ${r.year}` : ""} {r.trim ? `· ${r.trim}` : ""}</p>
          <p className="text-[11px] text-[var(--text-subtle)] mt-1">#{r.request_number} · {r.city || "كل المدن"}</p>
        </div>
        <span className="text-[11px] font-bold px-2.5 py-1 rounded-lg shrink-0" style={{ backgroundColor: `${color}22`, color }}>{lbl}</span>
      </div>
      <div className="mt-3 flex items-center justify-between pt-3 border-t" style={{ borderColor: "var(--border-c)" }}>
        <span className="text-sm text-[var(--amber)] font-bold" data-testid={`offers-count-${r.id}`}>{r.offers_count} عرض</span>
        <button data-testid={`toggle-offers-${r.id}`} onClick={onToggle} className="flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-white">
          {expanded ? "إخفاء العروض" : "عرض العروض"} <ChevronDown size={16} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3">
          {!detail ? <Loader /> : detail.offers.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] text-center py-4">لم تصل عروض بعد. سيتم إشعارك عند وصول عرض.</p>
          ) : detail.offers.map((o) => (
            <OfferRow key={o.id} req={r} offer={o} canAccept={r.status === "open"} onAccepted={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

function OfferRow({ req, offer, canAccept, onAccepted }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [payMethod, setPayMethod] = useState("cod");
  const [delivery, setDelivery] = useState("pickup");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const accepted = offer.status === "accepted";
  const declined = offer.status === "declined";

  const accept = async () => {
    if (delivery === "delivery" && !address.trim()) { toast.error("أدخل عنوان التوصيل"); return; }
    setBusy(true);
    try {
      const { data: order } = await api.post(`/part-requests/${req.id}/accept`, {
        offer_id: offer.id, payment_method: payMethod, delivery_method: delivery, address,
      });
      if (payMethod === "online") {
        const { data } = await api.post("/payments/tap/charge", { order_id: order.id });
        toast.info("جارٍ التحويل لبوابة الدفع...");
        window.location.assign(data.checkout_url);
        return;
      }
      toast.success("تم قبول العرض وإنشاء طلبك! الدفع عند الاستلام.");
      setOpen(false); onAccepted(); nav("/orders");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className={`rounded-xl border p-3 ${accepted ? "border-[var(--emerald)]" : "border-[var(--border-c)]"} ${declined ? "opacity-50" : ""}`} data-testid={`offer-row-${offer.id}`}>
      <div className="flex items-start gap-3">
        {offer.images?.[0] && <img src={fileUrl(offer.images[0])} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-white text-sm truncate">{offer.yard?.name || offer.yard_name}</span>
            {offer.yard?.verified && <ShieldCheck size={14} className="text-[var(--emerald)]" />}
          </div>
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mt-1">
            <Stars value={offer.yard?.rating_avg || 0} size={11} /><span>({offer.yard?.rating_count || 0})</span>
            {offer.yard?.city && <span className="flex items-center gap-0.5"><MapPin size={11} />{offer.yard.city}</span>}
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">الحالة: <span className="text-white">{offer.condition}</span>{offer.warranty ? ` · ضمان: ${offer.warranty}` : ""}</p>
          {offer.notes && <p className="text-xs text-[var(--text-subtle)] mt-1">{offer.notes}</p>}
        </div>
        <div className="text-left shrink-0">
          <p className="font-head text-lg font-black text-[var(--amber)]">{offer.price.toLocaleString("ar")}</p>
          <p className="text-[10px] text-[var(--text-muted)]">{SAR}</p>
        </div>
      </div>
      <div className="mt-3">
        {accepted ? (
          <span className="text-xs font-bold text-[var(--emerald)]">✓ تم قبول هذا العرض</span>
        ) : canAccept ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <button data-testid={`accept-offer-${offer.id}`} onClick={() => setOpen(true)} className="btn-amber w-full justify-center !py-2 text-sm">
              <ShoppingCart size={16} /> اقبل العرض واشترِ
            </button>
            <DialogContent className="bg-[var(--bg-card)] border-[var(--border-c)] text-white max-w-md" dir="rtl">
              <DialogHeader><DialogTitle className="text-white text-right">تأكيد الشراء</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="ts-card p-3 flex items-center justify-between">
                  <div><p className="text-sm font-bold text-white">{req.part_name}</p><p className="text-xs text-[var(--text-muted)]">{offer.yard?.name || offer.yard_name}</p></div>
                  <p className="font-head text-lg font-black text-[var(--amber)]">{offer.price.toLocaleString("ar")} {SAR}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">طريقة الاستلام</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[["pickup", "استلام من التشليح"], ["delivery", "توصيل"]].map(([v, l]) => (
                      <button key={v} data-testid={`accept-delivery-${v}`} onClick={() => setDelivery(v)}
                        className={`rounded-xl py-2.5 text-sm border ${delivery === v ? "border-[var(--amber)] text-[var(--amber)] bg-[var(--amber)]/10" : "border-[var(--border-c)] text-[var(--text-muted)]"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                {delivery === "delivery" && (
                  <input placeholder="عنوان التوصيل" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="accept-address"
                    className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none" />
                )}
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">طريقة الدفع</label>
                  <div className="grid grid-cols-1 gap-2">
                    {[["cod", "الدفع عند الاستلام"], ["online", "الدفع الإلكتروني (مدى / Visa / Mastercard)"]].map(([v, l]) => (
                      <button key={v} data-testid={`accept-pay-${v}`} onClick={() => setPayMethod(v)} type="button"
                        className={`text-right rounded-xl px-3 py-2.5 border ${payMethod === v ? "border-[var(--amber)] bg-[var(--amber)]/10 text-[var(--amber)]" : "border-[var(--border-c)] text-white"}`}>
                        <span className="block text-sm font-bold">{l}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <button data-testid="confirm-accept-offer" onClick={accept} disabled={busy} className="btn-amber w-full justify-center disabled:opacity-60">
                  {busy ? "جارٍ المعالجة..." : (payMethod === "online" ? "المتابعة للدفع الإلكتروني" : "تأكيد الطلب")}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        ) : (
          <span className="text-xs text-[var(--text-muted)]">{declined ? "لم يُختَر" : ""}</span>
        )}
      </div>
    </div>
  );
}
